
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase/client";
import { User, UserCheck, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

const Register = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  
  const formSchema = z.object({
    firstName: z.string().min(2, t("auth.firstName") + " must be at least 2 characters"),
    lastName: z.string().min(2, t("auth.lastName") + " must be at least 2 characters"),
    email: z.string().email("Please enter a valid email address"),
    phone: z.string().min(10, "Please enter a valid phone number"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
    termsAccepted: z.boolean().refine(val => val === true, {
      message: "You must accept the terms and conditions",
    }),
  }).refine(data => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

  type FormValues = z.infer<typeof formSchema>;
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      termsAccepted: false,
    },
  });
  
  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    
    try {
      // Sign up with Supabase Auth - this will trigger our profile creation trigger
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            first_name: values.firstName,
            last_name: values.lastName,
            phone: values.phone
          }
        }
      });
      
      if (authError) {
        toast.error(t("auth.registrationFailed"), {
          description: authError.message || t("auth.registrationFailedMessage"),
        });
        return;
      }
      
      // The profile will be created automatically via the database trigger
      // But we can update it manually here as well to ensure the data is set
      if (authData.user) {
        // Check if profile exists and update it
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: authData.user.id,
            first_name: values.firstName,
            last_name: values.lastName,
            phone: values.phone
          });
          
        if (profileError) {
          console.error("Failed to update user profile:", profileError);
          // Don't show an error to the user since the trigger should handle it
        }
      }
      
      toast.success(t("auth.registrationSuccess"), {
        description: t("auth.registrationSuccessMessage"),
      });
      
      navigate("/login");
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(t("auth.registrationFailed"), {
        description: t("auth.registrationFailedMessage"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Layout>
      <section className="py-12 md:py-20 bg-gradient-to-b from-clay-50 to-background">
        <div className="container max-w-xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-card border border-border rounded-xl p-8 shadow-sm"
          >
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-clay-100 text-clay-800 rounded-full mb-4">
                {t("auth.joinUs")}
              </span>
              <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">{t("auth.registerTitle")}</h1>
              <p className="text-muted-foreground">
                {t("auth.registerSubtitle")}
              </p>
            </div>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("auth.firstName")}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                            <Input placeholder={t("auth.firstNamePlaceholder")} className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("auth.lastName")}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                            <Input placeholder={t("auth.lastNamePlaceholder")} className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.email")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input type="email" placeholder={t("auth.emailPlaceholder")} className="pl-9" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.phone")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("auth.phonePlaceholder")} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.password")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input type="password" placeholder={t("auth.passwordPlaceholder")} className="pl-9" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.confirmPassword")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <UserCheck className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                          <Input type="password" placeholder={t("auth.confirmPasswordPlaceholder")} className="pl-9" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="termsAccepted"
                  render={({ field }) => (
                    <FormItem className="flex items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm">
                          {t("auth.termsAndConditions")}
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
                
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? t("auth.creatingAccount") : t("auth.createAccount")}
                </Button>
              </form>
            </Form>
            
            <div className="mt-6">
              <div className="flex items-center gap-2 my-4">
                <Separator className="flex-grow" />
                <span className="text-xs text-muted-foreground">{t("auth.or")}</span>
                <Separator className="flex-grow" />
              </div>
              
              <p className="text-center text-sm text-muted-foreground">
                {t("auth.haveAccount")}{" "}
                <Link to="/login" className="text-primary hover:underline">
                  {t("auth.logIn")}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Register;
